import { useState, KeyboardEvent, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Send } from 'lucide-react'
import { cn } from '@/lib/utils'

interface ChatInputProps {
  onSend: (message: string) => void
  disabled?: boolean
}

export const ChatInput = forwardRef<HTMLInputElement, ChatInputProps>(
  function ChatInput({ onSend, disabled }, ref) {
    const { t } = useTranslation('common')
    const [input, setInput] = useState('')

    const handleSend = () => {
      if (input.trim() && !disabled) {
        onSend(input)
        setInput('')
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    }

    return (
      <div className="border-t border-[var(--color-graphite)] p-3 bg-[var(--color-ink)]">
        <div className="flex gap-2 items-center">
          <input
            ref={ref}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={t('chatbot.placeholder')}
            disabled={disabled}
            className={cn(
              'flex-1 px-4 py-2.5 text-sm',
              'border border-[var(--color-graphite)] rounded-lg',
              'bg-[var(--color-charcoal)] text-white',
              'focus:outline-none focus:border-[var(--accent-primary)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'placeholder:text-[var(--color-stone)]'
            )}
          />
          <button
            type="button"
            onClick={handleSend}
            disabled={disabled || !input.trim()}
            className={cn(
              'p-2.5 bg-[var(--accent-primary)] text-white rounded-lg flex-shrink-0',
              'hover:bg-[var(--color-copper-400)] transition-colors',
              'disabled:opacity-50 disabled:cursor-not-allowed'
            )}
            aria-label={t('chatbot.send')}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>
    )
  }
)
